import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import history from 'browserHistory'
import ReactMarkdown from 'react-markdown'

import { Text } from 'components/base'
import { Button } from 'components/base'
import IntegrationDetails from './IntegrationDetails'

import { selectors as AuthSelectors } from 'data/auth'
import { selectors as UserSelectors } from 'data/users'
import { selectors as ConfigSelectors } from 'data/config'
import { selectors as OrganizationsSelectors } from 'data/organizations'

import CookiesHelpers from 'helpers/Cookies'
import RoutesHelper, { getAbsolutePath } from 'helpers/RoutesHelper'
import ApplicationsAPI from 'remote/applications'
import { createErrorToasterMessage } from 'data/ui/actions'

import BackToIntegrations from '../components/BackToIntegrations'

class ApplicationDetailsPage extends React.PureComponent {
  static propTypes = {
    userId: PropTypes.string.isRequired,
    apiToken: PropTypes.string.isRequired,
    apiEndpoint: PropTypes.string.isRequired,
    appDomain: PropTypes.string.isRequired,
    cookieDomain: PropTypes.string.isRequired,
    organizationId: PropTypes.string.isRequired,
    createErrorToasterMessage: PropTypes.func.isRequired,
  }

  parsedUrl = null

  state = {
    error: false,
    loading: true,
    applicationDetails: null,
  }

  constructor(props) {
    super(props)

    this.handleInstallClick = this.handleInstallClick.bind(this)
    this.handleRevokationClick = this.handleRevokationClick.bind(this)
  }

  getRedirectUri() {
    const { appDomain } = this.props
    const { id } = this.state.applicationDetails
    const defaultUrl = getAbsolutePath(
      RoutesHelper.settingsIntegrationApplicationDetails({
        applicationId: id,
      })
    )

    try {
      const url = this.parsedUrl.get('redirect_uri')
      const envIsDevelopmentWithWebpack = url.includes('lolcat.host:8080')

      const parts = new URL(
        envIsDevelopmentWithWebpack
          ? // Helpful when developping using webpack to avoid weird shenanigans
            // Because appDomain is lolcat.host:8000
            url.replace(':8080', ':8000')
          : url
      )

      // We're redirecting internally only
      if (parts.host.endsWith(appDomain)) {
        return url
      }

      return defaultUrl
    } catch (error) {
      // * URL constructor throws if URL is not absolute and second parameter is not specified.
      return defaultUrl
    }
  }

  async componentDidMount() {
    this.parsedUrl = new URLSearchParams(window.location.search)

    try {
      const result = await ApplicationsAPI.getApplicationById(
        {
          apiToken: this.props.apiToken,
          apiEndpoint: this.props.apiEndpoint,
        },
        { id: this.parsedUrl.get('applicationId') }
      )

      this.setState({
        loading: false,
        applicationDetails: result,
      })
    } catch (error) {
      this.props.createErrorToasterMessage({
        title: 'An error has occured while loading the application',
        detail:
          'Please try refreshing the page, or reach out to the support if the problem persists',
      })
    }
  }

  async handleRevokationClick() {
    const { apiToken, apiEndpoint } = this.props
    const { integration } = this.state.applicationDetails

    try {
      await ApplicationsAPI.revokeIntegrationById(
        { apiToken, apiEndpoint },
        { integrationId: integration.id }
      )
      history.push(RoutesHelper.settingsIntegrations())
    } catch (_) {
      this.props.createErrorToasterMessage({
        title: 'An error has occured while revoking the integration',
        detail:
          'Please try again or reach out to the support if the problem persists',
      })
    }
  }

  async handleInstallClick() {
    const { id } = this.state.applicationDetails
    const { cookieDomain, organizationId } = this.props

    CookiesHelpers.set('integrating-with-orgId', organizationId, {
      domain: cookieDomain,
    })

    window.location.href = getAbsolutePath(
      RoutesHelper.settingsIntegrationInstallApplication({
        applicationId: id,
      })
    )
  }

  render() {
    if (this.state.loading) {
      return (
        <React.Fragment>
          <BackToIntegrations />
          <p>Loading...</p>
        </React.Fragment>
      )
    }

    const {
      photoURL,
      name,
      description,
      integration,
    } = this.state.applicationDetails

    const installIncentive = Boolean(integration) ? 'Reinstall' : 'Install'

    return (
      <React.Fragment>
        <BackToIntegrations />

        {integration && (
          <IntegrationDetails {...integration} applicationName={name} />
        )}

        <section className="application-header flexbox">
          <div
            className="margin-right-large"
            style={{
              boxShadow: '0 2px 24px 0 rgba(0,0,0,0.06)',
            }}
          >
            <img
              src={photoURL}
              width={100}
              height={100}
              alt="application logo"
            />
          </div>
          <div className="flexbox flexbox-justify-center flexbox-column">
            <Text.Subtitle className="margin-bottom-normal">
              {name}
            </Text.Subtitle>
            <div className="flexbox">
              <Button.Primary
                className="margin-right-normal"
                onClick={this.handleInstallClick}
              >
                {installIncentive}
              </Button.Primary>
              {integration && (
                <Button.Danger onClick={this.handleRevokationClick}>
                  Revoke
                </Button.Danger>
              )}
            </div>
          </div>
        </section>
        <ReactMarkdown
          className="margin-top-large shade-10 font-size-large line-height-larger"
          source={description}
          escapeHtml={true}
          renderers={{
            heading(props) {
              return (
                <React.Fragment>
                  <br />
                  <Text.Subtitle className="shade-10 margin-vertical-normal">
                    {props.children}
                  </Text.Subtitle>
                </React.Fragment>
              )
            },

            list(props) {
              if (props.ordered) {
                return (
                  <ol className="padding-left-medium margin-vertical-normal">
                    {props.children}
                  </ol>
                )
              }

              return (
                <ul className="padding-left-medium margin-vertical-normal line-height-larger">
                  {props.children}
                </ul>
              )
            },

            listItem(props) {
              return (
                <li
                  style={{
                    listStyle: 'disc',
                  }}
                >
                  {props.children}
                </li>
              )
            },
            image(props) {
              return (
                <img
                  src={props.src}
                  alt={props.alt}
                  title={props.title}
                  className="margin-vertical-normal"
                  style={{
                    maxWidth: '100%',
                  }}
                />
              )
            },
            paragraph(props) {
              return <p className="margin-vertical-small">{props.children}</p>
            },
          }}
        />
      </React.Fragment>
    )
  }
}

export default connect(
  state => {
    return {
      apiToken: AuthSelectors.getApiToken(state),
      userId: UserSelectors.getCurrentUserId(state),
      apiEndpoint: ConfigSelectors.getApiEndpoint(state),
      appDomain: ConfigSelectors.getAppDomain(state),
      cookieDomain: ConfigSelectors.getCookieDomain(state),
      organizationId: OrganizationsSelectors.getOrganizationId(state),
    }
  },
  { createErrorToasterMessage }
)(ApplicationDetailsPage)
